import * as React from "react";
import type { DragPosition, UseResponsivePopoverDragResult } from "./types";

interface UseResponsivePopoverDragOptions {
  draggable: boolean;
  open?: boolean;
}

/**
 * Hook encapsulating the draggable behavior used by the desktop popover.
 *
 * Preserves exact effect/hook order, dependency arrays, and conditional logic
 * from the original inline implementation.
 */
export function useResponsivePopoverDrag({
  draggable,
  open,
}: UseResponsivePopoverDragOptions): UseResponsivePopoverDragResult {
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState<DragPosition>({ x: 0, y: 0 });
  const [position, setPosition] = React.useState<DragPosition>({ x: 0, y: 0 });
  const dragRef = React.useRef<HTMLDivElement>(null);
  const dragHandleRef = React.useRef<HTMLButtonElement>(null);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      if (!draggable || !dragRef.current) return;

      const rect = dragRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    },
    [draggable]
  );

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragRef.current) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain to viewport
      const rect = dragRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      setPosition({
        x: Math.max(0, Math.min(maxX, newX)),
        y: Math.max(0, Math.min(maxY, newY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Reset position when popover closes
  React.useEffect(() => {
    if (!open) {
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [open]);

  return {
    isDragging,
    position,
    dragRef,
    dragHandleRef,
    handleMouseDown,
  };
}
